/**
 * Voice AI — Guest Service
 * API calls for guest memory: list, detail, profile, conversations, memories.
 */

import { authService } from './auth-service.js';

const API_BASE = '/voice/php/api';

class GuestService {

  /** List all guests (paginated, searchable). */
  async listGuests(opts = {}, { signal } = {}) {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.sort) params.set('sort', opts.sort);
    if (opts.sort_dir) params.set('sort_dir', opts.sort_dir);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));

    const fetchOpts = {};
    if (signal) fetchOpts.signal = signal;
    const res = await authService.apiFetch(`${API_BASE}/guests.php?${params}`, fetchOpts);
    return res.json();
  }

  /** Get single guest detail (profile + memories + recent conversations). */
  async getGuest(phone) {
    const res = await authService.apiFetch(`${API_BASE}/guests.php?phone=${encodeURIComponent(phone)}`);
    return res.json();
  }

  /** Update guest profile. */
  async updateProfile(phone, data) {
    const res = await authService.apiFetch(`${API_BASE}/guests.php?phone=${encodeURIComponent(phone)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  /** Get paginated conversations for a guest. */
  async getConversations(phone, opts = {}) {
    const params = new URLSearchParams({ phone });
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));

    const res = await authService.apiFetch(`${API_BASE}/guest-conversations.php?${params}`);
    return res.json();
  }

  /** Get single conversation with full transcript. */
  async getConversationDetail(phone, callId) {
    const params = new URLSearchParams({ phone, call_id: String(callId) });
    const res = await authService.apiFetch(`${API_BASE}/guest-conversations.php?${params}`);
    return res.json();
  }

  /** List all memories for a guest. */
  async listMemories(phone) {
    const res = await authService.apiFetch(`${API_BASE}/guest-memory.php?phone=${encodeURIComponent(phone)}`);
    return res.json();
  }

  /** Add a memory (manual). */
  async addMemory(data) {
    const res = await authService.apiFetch(`${API_BASE}/guest-memory.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  /** Update a memory. */
  async updateMemory(phone, id, data) {
    const res = await authService.apiFetch(`${API_BASE}/guest-memory.php?phone=${encodeURIComponent(phone)}&id=${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  }

  /** Delete a memory. */
  async deleteMemory(phone, id) {
    const res = await authService.apiFetch(`${API_BASE}/guest-memory.php?phone=${encodeURIComponent(phone)}&id=${id}`, {
      method: 'DELETE',
    });
    return res.json();
  }

  /** Format phone number for display: +14095551234 → (409) 555-1234 */
  formatPhone(phone) {
    if (!phone) return '';
    const digits = phone.replace(/[^0-9]/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  }

  /** Relative time display. */
  relativeTime(dateStr) {
    if (!dateStr) return 'Never';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return `${Math.floor(diffDay / 30)}mo ago`;
  }

  /** Format duration seconds → "2m 34s" */
  formatDuration(seconds) {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  }
}

export const guestService = new GuestService();
